import { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Draggable } from '@hello-pangea/dnd';
import InvestorLogo from '../../../components/data/InvestorLogo';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import DraggableItemMenu from './DraggableItemMenu';
import { CircleHelp, Ellipsis, DollarSign, NotepadText } from 'lucide-react';
import FormattedNumberInput from './FormattedNumberInput';
import { useCRM } from '../../../hooks/crm';
import DraggableItemInsights from './DraggableItemInsights';

const NoteSection = memo(({ isEditing, note, setNote, onSave, onCancel, t }) => {
  if (isEditing) {
    return (
      <div className="mt-2">
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="w-full p-2 border rounded-md"
          rows="3"
          placeholder={t('app:crm.t38')}
        />
        <div className="mt-4 flex justify-start space-x-6">
          <button onClick={onSave} className="btn btn-xs cursor-pointer">
            {t('app:crm.t14')}
          </button>
          <button onClick={onCancel} className="btn btn-xs cursor-pointer">
            {t('app:crm.t5')}
          </button>
        </div>
      </div>
    );
  }

  return note ? (
    <div className="flex items-center mt-2">
      <NotepadText strokeWidth={1.5} className="w-4 h-4 mr-2" />
      <p className="mx-0 my-0 text-sm text-clr9">{note}</p>
    </div>
  ) : null;
});

const ValueSection = memo(({ isEditing, value, onSave, onCancel }) => {
  if (isEditing) {
    return (
      <FormattedNumberInput initialValue={value || '0.00'} onSave={onSave} onCancel={onCancel} />
    );
  }

  return value !== null && value !== undefined && parseFloat(value) > 0 ? (
    <div className="flex items-center mt-4">
      <DollarSign strokeWidth={1.5} className="w-4 h-4 mr-2" />
      <p className="mx-0 my-0 text-sm">{formatNumber(value)}</p>
    </div>
  ) : null;
});

function formatNumber(value) {
  if (value === null || value === undefined) return '0.00';

  const numericValue = parseFloat(value).toFixed(2);
  const parts = numericValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

const DraggableItem = ({
  investor,
  index,
  boardId,
  columnId,
  disableDnD = false,
}) => {
  const { t } = useTranslation();
  const { updateInvestorNote, updateInvestorValue } = useCRM();

  const [isEditingNote, setIsEditingNote] = useState(false);
  const [note, setNote] = useState(investor.note || '');
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [value, setValue] = useState(investor.value || 0);
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  useEffect(() => {
    setNote(investor.note || '');
  }, [investor.note]);

  useEffect(() => {
    setValue(investor.value != null ? investor.value : 0);
  }, [investor.value]);

  useEffect(() => {
    setIsInsightsOpen(false);
  }, [investor.investor_id]);

  const handleSaveNote = useCallback(async () => {
    try {
      await updateInvestorNote({
        boardId,
        columnId,
        investorId: investor.investor_id,
        note,
      });
      setIsEditingNote(false);
    } catch (error) {
      console.error(error);
    }
  }, [boardId, columnId, investor.investor_id, note, updateInvestorNote]);

  const handleCancelEditNote = useCallback(() => {
    setIsEditingNote(false);
    setNote(investor.note || '');
  }, [investor.note]);

  const handleSaveValue = useCallback(async (numericValue) => {
    try {
      await updateInvestorValue({
        boardId,
        columnId,
        investorId: investor.investor_id,
        value: numericValue,
      });
      setValue(numericValue);
      setIsEditingValue(false);
    } catch (error) {
      console.error(error);
    }
  }, [boardId, columnId, investor.investor_id, updateInvestorValue]);

  const handleCancelEditValue = useCallback(() => {
    setIsEditingValue(false);
    setValue(investor.value || 0);
  }, [investor.value]);

  const cardContent = (snapshot = { isDragging: false }, dragProps = {}) => (
    <div
      ref={dragProps.ref}
      {...dragProps.draggableProps}
      {...dragProps.dragHandleProps}
      className={`${disableDnD ? '' : 'cursor-grab'} min-h-24 p-3 bg-white rounded-md mb-4 shadow relative ${
        snapshot.isDragging ? 'dragging' : ''
      }`}
    >
      {investor && investor.investor_id ? (
        <>
          <div className="flex items-start">
            <div className="flex-1 flex items-start">
              <InvestorLogo
                id={investor.details?.id}
                country={investor.details?.country}
                className="w-6 h-6 flex-shrink-0"
                alt={`${investor.details?.name} logo`}
              />
              <div className="ml-3 min-w-0 flex-1">
                <h4 className="text-sm font-bold break-words">
                  {investor.details?.name || 'Unnamed Investor'}
                </h4>
              </div>
            </div>

            <div className="ml-3 flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsInsightsOpen(true)}
                className="flex h-5 w-5 cursor-pointer items-center justify-center text-stone-500 hover:text-stone-900"
                title={t('app:crm.t51')}
                aria-label={t('app:crm.t51')}
              >
                <CircleHelp strokeWidth={1.5} className="h-4 w-4" />
              </button>

              <DraggableItemMenu
                boardId={boardId}
                columnId={columnId}
                investorId={investor.investor_id}
                investorIdField={investor.details?.id}
                investorName={investor.details?.name}
                onEditNote={() => setIsEditingNote(true)}
                onEditValue={() => setIsEditingValue(true)}
                note={note}
                value={value}
                menuTrigger={(
                  <button
                    type="button"
                    className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                    onPointerDown={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    title={t('app:crm.t71')}
                    aria-label={t('app:crm.t71')}
                  >
                    <Ellipsis strokeWidth={1.5} className="h-5 w-5" />
                  </button>
                )}
              />
            </div>
          </div>

          <div className="absolute bottom-2 right-2">
            {investor.details?.country ? (
              <CountryFlag countryName={investor.details?.country} />
            ) : (
              <span className="text-xs text-gray-500 italic">No country</span>
            )}
          </div>

          <NoteSection
            isEditing={isEditingNote}
            note={note}
            setNote={setNote}
            onSave={handleSaveNote}
            onCancel={handleCancelEditNote}
            t={t}
          />

          <ValueSection
            isEditing={isEditingValue}
            value={value}
            onSave={handleSaveValue}
            onCancel={handleCancelEditValue}
          />

          <DraggableItemInsights
            investor={investor.details}
            isOpen={isInsightsOpen}
            onClose={() => setIsInsightsOpen(false)}
            t={t}
          />
        </>
      ) : (
        <p>{t('app:crm.t15')}</p>
      )}
    </div>
  );

  if (disableDnD) {
    return cardContent();
  }

  return (
    <Draggable
      key={`${columnId}-${investor.investor_id}`}
      draggableId={`${columnId}-${investor.investor_id}`}
      index={index}
    >
      {(provided, snapshot) => cardContent(snapshot, {
        ref: provided.innerRef,
        draggableProps: provided.draggableProps,
        dragHandleProps: provided.dragHandleProps,
      })}
    </Draggable>
  );
};

export default DraggableItem;
